import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useDocumentStore } from '@roc/feature-documents';
import { InspectionReport } from '@roc/feature-inspections';
import { Document } from '@roc/feature-types';
import { StandardDialog, ConfirmDialog, Button } from '@roc/ui';
import { useLoanStore } from '@roc/feature-loans-shared';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useDrawsStore } from '../hooks';
import { PointOfContactV2 } from '../pointOfContactV2/pointOfContactV2';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import { LoanProcess } from '@roc/feature-utils';

interface PointOfContactDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document;
  isInternalValuationPropertyPhotosContactFlow?: boolean;
  isManualUploadMode?: boolean;
}

export const TakePropertyPhotosDialog = observer(
  (props: PointOfContactDialogProps) => {
    return <InspectionReportDialog {...props} />
  }
);

export const PointOfContactDialogV2 = observer(
  ({ open, onClose, document, isInternalValuationPropertyPhotosContactFlow }: PointOfContactDialogProps) => {
    const { documentStore } = useDocumentStore();
    const { drawStore } = useDrawsStore();
    const { takePropertyPhotosStore } = documentStore.documentFormStore;
    const { pointOfContactV2Store } = takePropertyPhotosStore;
    const { pointOfContactFormStore } = pointOfContactV2Store;
    const { loanStore } = useLoanStore();
    const address = isInternalValuationPropertyPhotosContactFlow ? document?.collateralAddress : (drawStore.drawDTO?.propertyAddress ?? '');
    const loanId: number = drawStore.drawDTO?.draw?.loanId || loanStore.loanDetails?.loanId;
    const taskId = document?.taskId;

    useEffect(() => {
      if (open) {
        pointOfContactV2Store.initialize(loanId);
      }
    }, [loanId, open]);

    const submit = async () => {
      await pointOfContactV2Store.submit(loanId, [document.taskId], address);
      if (pointOfContactFormStore.form.meta.isValid) {
        onClose();
      }
    };

    const getActions = () => {
      return (
        <>
          <Button
            style={{ marginRight: '8px' }}
            onClick={onClose}
            color="primary"
            variant="outlined"
            testId="cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            color="primary"
            variant="contained"
            testId="confirm"
            id="confirm"
          >
            Confirm
          </Button>
        </>
      );
    }

    return (
      <>
        {taskId ? (
          <CustomTaskDialog
            open={open}
            title={'Send Task'}
            handleClose={onClose}
            removeContentBox
            dialogContent={
              <PointOfContactV2 pointOfContactV2Store={pointOfContactV2Store} isInternalValuationPropertyPhotosContactFlow={isInternalValuationPropertyPhotosContactFlow} />
            }
            dialogActions={getActions()}
            taskId={taskId}
          />
        ) : (
          <ConfirmDialog
            open={open}
            title={'Send Task'}
            handleClose={onClose}
            removeContentBox
            body={
              <PointOfContactV2 pointOfContactV2Store={pointOfContactV2Store} isInternalValuationPropertyPhotosContactFlow={isInternalValuationPropertyPhotosContactFlow} />
            }
            handleCancel={onClose}
            handleConfirm={submit}
          />
        )}
      </>
    );
  }
);

export const InspectionReportDialog = observer(
  ({ open, onClose, document, isInternalValuationPropertyPhotosContactFlow, isManualUploadMode }: PointOfContactDialogProps) => {
    const inspectionId = document?.inspectionId;
    const taskId = document?.taskId;
    const theme = useTheme();
    const isDrawsTask = LoanProcess.DRAWS.toLowerCase() === document?.loanProcess?.toLowerCase();
    const externalId1 = document?.loanId;
    const externalId2 = isDrawsTask ? document?.drawId : document?.collateralId;


    return (
      <>
        {taskId ? (
          <CustomTaskDialog
            open={open}
            title={'Inspection Report'}
            handleClose={onClose}
            maxWidth={false}
            dialogContent={<InspectionReport
              isInternalValuationPropertyPhotosContactFlow={isInternalValuationPropertyPhotosContactFlow}
              isManualUploadMode={isManualUploadMode}
              inspectionId={inspectionId}
              externalId1={externalId1}
              externalId2={externalId2}
              style={{
                padding: 0,
                paddingRight: 8
              }} />}
            taskId={taskId}
          />
        ) : (
          <StandardDialog
            open={open}
            handleClose={onClose}
            maxWidth={false}
            dialogContent={<InspectionReport
              isInternalValuationPropertyPhotosContactFlow={isInternalValuationPropertyPhotosContactFlow}
              isManualUploadMode={isManualUploadMode}
              inspectionId={inspectionId}
              externalId1={externalId1}
              externalId2={externalId2} />}
          />
        )}
      </>
    );
  }
);
